import { type FormEvent, type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePageMeta } from '../../discovery/page-meta';
import { AppLink, useNavigator } from '../../discovery/router';
import { useSession } from '../../session/SessionProvider';
import { BackLink } from '../panels';
import { SiteShell } from '../SitePages';
import {
  type CartLine,
  type OfferCard,
  type OfferDetail,
  type Place,
  type ProviderCard,
  type Requirement,
  type Review,
  type Store,
  type TestRow,
  SessionApiError,
  getOffer,
  getProviders,
  getReviews,
  getStore,
  getTaxonomy,
  getTests,
  searchStore,
} from './store-api';
import { type Loaded, refusalOf, useCart, useLoad, useNarrow, usePlace, useToast } from './store-hooks';
import {
  eyebrowOf,
  hoursTable,
  initialsOf,
  legalOf,
  milesBetween,
  money,
  nextLabel,
  offersDelivery,
  openStatus,
  presentationLabel,
  priceLabel,
  savingBadge,
  sizeOf,
} from './store-model';
import { BookService, Consultation, PharmacyQuestions, ProductDialog } from './StoreDialogs';
import { FeesLine, type Mode, Soon, StoreBasket } from './StoreBasket';
import { Icon, PATHS, Stars } from './store-icons';
import './store.css';

/**
 * The Store page — website stage 5, built from `Just Treated Store.dc.html`.
 *
 * It replaces the old discovery storefront page on the same path,
 * `/storefronts/:slug`, so every link the homepage, search and product pages
 * already print keeps working and now lands here. Public, like a shop window:
 * browsing, search, the product sheet and the basket work logged out; sending
 * a pharmacy medicine's answers, a prescription consultation and confirming a
 * booking need an account, exactly as the platform requires.
 *
 * Every number on it is the platform's. What the frame draws with nothing
 * behind it is either left out or says "Coming soon" and does nothing — the
 * hand-off lists each one.
 */

const PLAQUE = 'linear-gradient(145deg,#6d3ffb,#4a22bd)';
const SORTS = [
  ['RELEVANCE', 'Recommended'],
  ['PRICE_ASC', 'Price: low to high'],
  ['PRICE_DESC', 'Price: high to low'],
] as const;
type Sort = (typeof SORTS)[number][0];
const REVIEW_PAGE = 20;

type Overlay =
  | { readonly kind: 'product'; readonly offer: OfferCard }
  | { readonly kind: 'questions'; readonly offer: OfferCard; readonly quantity: number }
  | { readonly kind: 'consult'; readonly offer: OfferCard }
  | { readonly kind: 'book'; readonly offer: OfferCard };

function cardOf(detail: OfferDetail): OfferCard {
  const { images, storefront, description: _description, categoryIds: _categoryIds, ...rest } = detail;
  return {
    ...rest,
    storefrontId: storefront.id,
    storefrontSlug: storefront.slug,
    storefrontName: storefront.name,
    ...(images[0] !== undefined ? { image: images[0] } : {}),
  };
}

function townOf(store: Store): string | undefined {
  const located = store.locations.find((l) => l.town !== undefined || l.postcode !== undefined);
  if (located === undefined) return undefined;
  return [located.town, located.postcode].filter(Boolean).join(', ');
}

function coordinatesOf(store: Store): { latitude: number; longitude: number } | undefined {
  return store.locations.find((l) => l.coordinates !== undefined)?.coordinates;
}

export function StorePage({ slug }: { readonly slug: string }): JSX.Element {
  const loaded = useLoad(`store:${slug}`, () => getStore(slug));
  const store = loaded.status === 'loaded' ? loaded.data : undefined;
  const { place, recent, choose } = usePlace();

  usePageMeta({
    title: store?.name ?? (loaded.status === 'error' ? 'Not found' : 'Store'),
    ...(store?.shortDescription !== undefined ? { description: store.shortDescription } : {}),
    ...(store !== undefined ? { canonicalPath: `/storefronts/${store.slug}` } : {}),
    noindex: store === undefined,
  });

  const cart = useCart();
  const count = store !== undefined && cart.cart?.storefrontId === store.id ? (cart.cart.itemCount ?? 0) : 0;
  const narrow = useNarrow();
  const [drawer, setDrawer] = useState(false);

  const header = {
    location: <LocationPicker place={place} recent={recent} choose={choose} />,
    ...(count > 0 && cart.cart !== undefined
      ? {
          basket: (
            <button
              type="button"
              className="jt-st-pill-basket"
              aria-label={`Basket, ${String(count)} ${count === 1 ? 'item' : 'items'}, ${money(cart.cart.subtotalMinor, cart.cart.currency)}`}
              onClick={() => {
                if (narrow) setDrawer(true);
                else
                  document
                    .querySelector('aside[aria-label="Basket"]')
                    ?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <span className="jt-st-pill-basket__icon">
                <Icon d={PATHS.basket} size={17} stroke="#fff" />
                <span>{count}</span>
              </span>
              <span className="jt-st-pill-basket__total">
                {money(cart.cart.subtotalMinor, cart.cart.currency)}
              </span>
            </button>
          ),
        }
      : {}),
  };

  if (loaded.status !== 'loaded' || store === undefined) {
    return (
      <SiteShell store={header}>
        <BackLink />
        {loaded.status === 'loading' ? (
          <p className="jt-st-muted" role="status">
            Loading this store…
          </p>
        ) : loaded.status === 'error' && loaded.notFound ? (
          <div className="jt-st-missing">
            <h1>We could not find that store.</h1>
            <p>
              It may no longer be listed.{' '}
              <AppLink className="jt-link" to="/search">
                Search the marketplace
              </AppLink>
              .
            </p>
          </div>
        ) : (
          <div className="jt-st-missing">
            <h1>We could not load this store.</h1>
            <p>{loaded.status === 'error' ? loaded.message : ''}</p>
            <button type="button" className="jt-st-pill" onClick={loaded.reload}>
              Try again
            </button>
          </div>
        )}
      </SiteShell>
    );
  }

  return (
    <SiteShell store={header}>
      <StoreBody
        store={store}
        place={place}
        cart={cart}
        narrow={narrow}
        drawer={drawer}
        setDrawer={setDrawer}
      />
    </SiteShell>
  );
}

// -- The header's location picker ------------------------------------------------

export function LocationPicker({
  place,
  recent,
  choose,
}: {
  readonly place: Place | undefined;
  readonly recent: readonly Place[];
  readonly choose: (postcode: string) => Promise<Place>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const label =
    place === undefined
      ? 'Enter your postcode'
      : [place.postcode, place.displayArea].filter(Boolean).join(', ');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onDown = (event: MouseEvent): void => {
      if (box.current !== null && !box.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const pick = async (postcode: string): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      await choose(postcode);
      setOpen(false);
      setText('');
    } catch (failure) {
      setError(
        failure instanceof SessionApiError && failure.status === 404
          ? 'We could not find that postcode.'
          : failure instanceof SessionApiError && failure.status === 400
            ? 'That does not look like a UK postcode.'
            : 'We couldn’t check this postcode right now.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="jt-st-loc" ref={box}>
      <button
        type="button"
        className="jt-st-loc__btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Change location: ${label}`}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon d={PATHS.pin} size={17} />
        <span className="jt-st-loc__label">{label}</span>
        <Icon d={PATHS.chevronDown} size={14} stroke="#17112b" width={2.6} />
      </button>
      {open ? (
        <div role="dialog" aria-modal="false" aria-labelledby="jt-st-loc-title" className="jt-st-loc__pop">
          <div className="jt-st-loc__head">
            <span id="jt-st-loc-title">Enter your location</span>
            <button type="button" className="jt-st-round" aria-label="Close" onClick={() => setOpen(false)}>
              <Icon d={PATHS.close} size={16} stroke="#17112b" width={2.6} />
            </button>
          </div>
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              if (text.trim().length > 0) void pick(text);
            }}
          >
            <label className="jt-st-loc__field">
              <span>Postcode or address</span>
              <input
                autoFocus
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Enter your postcode"
                autoComplete="postal-code"
              />
              {text.trim().length > 0 ? (
                <button type="submit" disabled={busy}>
                  {busy ? 'Finding…' : 'Search'}
                </button>
              ) : null}
            </label>
            {error !== '' ? (
              <span role="alert" className="jt-st-q__err">
                {error}
              </span>
            ) : null}
          </form>
          <div className="jt-st-loc__geo" aria-disabled="true">
            <Icon d={PATHS.locate} size={17} />
            Use your location <Soon />
          </div>
          {recent.length > 0 ? (
            <div className="jt-st-loc__recent">
              <span>Recent searches</span>
              {recent.map((r) => (
                <button key={r.postcode} type="button" onClick={() => void pick(r.postcode)}>
                  <Icon d={PATHS.clock} size={16} stroke="#8a7fb5" />
                  <span>
                    <b>{r.postcode}</b>
                    {r.displayArea !== undefined ? <small>{r.displayArea}</small> : null}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// -- The page body -------------------------------------------------------------------

function StoreBody({
  store,
  place,
  cart,
  narrow,
  drawer,
  setDrawer,
}: {
  readonly store: Store;
  readonly place: Place | undefined;
  readonly cart: ReturnType<typeof useCart>;
  readonly narrow: boolean;
  readonly drawer: boolean;
  readonly setDrawer: (open: boolean) => void;
}): JSX.Element {
  const session = useSession();
  const signedIn = session.status === 'signedIn' && session.user !== undefined;
  const navigator = useNavigator();
  const [toast, flash] = useToast();
  const [overlay, setOverlayState] = useState<Overlay | undefined>(undefined);
  const [mode, setMode] = useState<Mode>(offersDelivery(store) ? 'DELIVERY' : 'COLLECTION');
  const [basketError, setBasketError] = useState('');
  const [section, setSection] = useState('products');
  const [shelf, setShelf] = useState<string | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('RELEVANCE');
  const [form, setForm] = useState<string | undefined>(undefined);
  const now = useMemo(() => new Date(), []);

  const services = useLoad(`svc:${store.id}`, () =>
    searchStore({ storefrontId: store.id, kind: 'SERVICE', limit: 48 }),
  );
  const taxonomy = useLoad('taxonomy', () => getTaxonomy());
  const tests = useLoad('tests', () => getTests());
  const providers = useLoad(`providers:${place?.postcode ?? ''}`, () => getProviders(place?.postcode));
  const [reviewLimit, setReviewLimit] = useState(REVIEW_PAGE);
  const reviews = useLoad(`reviews:${store.id}:${String(reviewLimit)}`, () =>
    getReviews(store.id, reviewLimit),
  );

  const term = query.trim();
  const showProducts = shelf !== undefined || term.length >= 2;
  const productKey = showProducts ? `p:${store.id}:${shelf ?? ''}:${term}:${sort}` : undefined;
  const [extra, setExtra] = useState<{ key: string; offers: readonly OfferCard[]; cursor?: string }>({
    key: '',
    offers: [],
  });
  const products = useLoad(productKey, () =>
    searchStore({
      storefrontId: store.id,
      kind: 'PRODUCT',
      ...(term.length >= 2 ? { q: term } : {}),
      ...(shelf !== undefined ? { categoryId: shelf } : {}),
      sort,
    }),
  );

  // Keep the overlay in the address, so "Log in to continue" brings the visitor
  // back to the same product or service, as the frame promises.
  const setOverlay = useCallback(
    (next: Overlay | undefined): void => {
      setOverlayState(next);
      const base = `/storefronts/${store.slug}`;
      const target =
        next === undefined
          ? base
          : next.kind === 'book'
            ? `${base}?book=${encodeURIComponent(next.offer.id)}`
            : `${base}?item=${encodeURIComponent(next.offer.id)}`;
      if (window.location.pathname + window.location.search !== target) {
        window.history.replaceState({}, '', target);
      }
    },
    [store.slug],
  );

  // Reopen what the address names (a return from Log in, or a shared link).
  const reopened = useRef(false);
  useEffect(() => {
    if (reopened.current) return;
    reopened.current = true;
    const item = navigator.location.query.get('item');
    const book = navigator.location.query.get('book');
    const listingId = item ?? book;
    if (listingId === null || !/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/.test(listingId)) return;
    getOffer(listingId)
      .then((detail) => {
        if (detail.storefront.id !== store.id) return;
        const offer = cardOf(detail);
        if (book !== null && offer.kind === 'SERVICE') setOverlayState({ kind: 'book', offer });
        else if (legalOf(offer) === 'POM') setOverlayState({ kind: 'consult', offer });
        else setOverlayState({ kind: 'product', offer });
      })
      .catch(() => undefined);
  }, [navigator.location.query, store.id]);

  const shelfName = (id: string | undefined): string | undefined =>
    store.categories.find((c) => c.id === id)?.name;
  const images = useMemo(() => {
    const map = new Map<string, string>();
    if (taxonomy.status === 'loaded') {
      for (const c of taxonomy.data.categories) if (c.imageUrl !== undefined) map.set(c.id, c.imageUrl);
    }
    return map;
  }, [taxonomy]);

  const lineFor = (listingId: string): CartLine | undefined =>
    cart.cart?.storefrontId === store.id ? cart.cart.lines.find((l) => l.listingId === listingId) : undefined;

  /** The one way into the basket. The server decides; a refusal is shown in its own words. */
  const add = async (offer: OfferCard, quantity: number, consultationId?: string): Promise<boolean> => {
    setBasketError('');
    try {
      await cart.add(offer.id, quantity, consultationId);
      flash(
        consultationId !== undefined
          ? 'Added to basket. A pharmacist will check your answers.'
          : 'Added to basket',
      );
      return true;
    } catch (error) {
      const refusal = refusalOf(error);
      if (refusal.code === 'CONSULTATION_REQUIRED' && consultationId === undefined) {
        // The pharmacy asks questions first — a pharmacy medicine the card could not tell apart.
        setOverlay(
          legalOf(offer) === 'POM' ? { kind: 'consult', offer } : { kind: 'questions', offer, quantity },
        );
        return false;
      }
      setBasketError(refusal.message);
      flash(refusal.message);
      if (narrow) setDrawer(true);
      return false;
    }
  };

  const setLine = async (line: CartLine, quantity: number): Promise<void> => {
    setBasketError('');
    try {
      await cart.setQuantity(line.id, Math.max(0, quantity));
      if (quantity <= 0) flash('Removed from basket');
    } catch (error) {
      const refusal = refusalOf(error);
      setBasketError(refusal.message);
    }
  };

  /** The card's "+": straight in for an ordinary product, through the questions for a pharmacy medicine. */
  const quickAdd = async (offer: OfferCard): Promise<void> => {
    const legal = legalOf(offer);
    if (legal === 'POM') {
      setOverlay({ kind: 'consult', offer });
      return;
    }
    if (legal === 'CHECK') {
      setOverlay({ kind: 'questions', offer, quantity: 1 });
      return;
    }
    const line = lineFor(offer.id);
    if (line !== undefined) await setLine(line, line.quantity + 1);
    else await add(offer, 1);
  };

  const buyFromSheet = async (
    offer: OfferCard,
    quantity: number,
    requirement: Requirement | undefined,
  ): Promise<void> => {
    const line = lineFor(offer.id);
    if (line !== undefined) {
      await setLine(line, quantity);
      flash('Basket updated');
      setOverlay(undefined);
      return;
    }
    if (requirement?.required === true || legalOf(offer) === 'CHECK') {
      setOverlay({ kind: 'questions', offer, quantity });
      return;
    }
    if (await add(offer, quantity)) setOverlay(undefined);
  };

  // -- Sections & the sticky nav ---------------------------------------------
  const svcList = services.status === 'loaded' ? services.data.results : [];
  const testRows: readonly TestRow[] =
    tests.status === 'loaded' ? tests.data.filter((row) => row.offer.storefrontId === store.id) : [];
  const tabs = [
    ['products', 'Products'],
    ['services', 'Services'],
    ...(testRows.length > 0 ? [['tests', 'Tests'] as const] : []),
    ['reviews', 'Reviews'],
    ['about', 'About'],
  ] as const;

  useEffect(() => {
    const onScroll = (): void => {
      let current = 'products';
      for (const [id] of tabs) {
        const el = document.getElementById(`st-${id}`);
        if (el !== null && el.getBoundingClientRect().top <= 170) current = id;
      }
      setSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  });

  const goSection = (id: string): void => {
    setSection(id);
    document.getElementById(`st-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const status = openStatus(store.openingHours, now);
  const where = townOf(store);
  const initials = initialsOf(store.name);
  const isPharmacy = store.registration !== undefined && /gphc/i.test(store.registration.regulator);
  const badges = [
    ...(isPharmacy ? ['GPhC registered'] : []),
    ...(store.registration !== undefined && /cqc/i.test(store.registration.regulator)
      ? ['CQC registered']
      : []),
  ];
  const storeCoords = coordinatesOf(store);
  const miles =
    place?.coordinates !== undefined && storeCoords !== undefined
      ? milesBetween(place.coordinates, storeCoords)
      : undefined;
  const next = svcList
    .filter((s) => s.bookability?.bookable === true && s.bookability.nextAvailableAt !== undefined)
    .sort((a, b) =>
      (a.bookability?.nextAvailableAt ?? '').localeCompare(b.bookability?.nextAvailableAt ?? ''),
    )[0];
  const terms = {
    delivery:
      store.deliveryFromMinor === undefined
        ? undefined
        : store.deliveryFromMinor === 0
          ? 'Free delivery'
          : `From ${money(store.deliveryFromMinor, store.deliveryCurrency ?? 'GBP')}`,
  };

  const facts = [
    ...(offersDelivery(store)
      ? [
          {
            label: 'Delivery',
            value: store.deliveryEtaLabel ?? 'Offered',
            sub: terms.delivery ?? '',
            icon: PATHS.van,
          },
        ]
      : []),
    ...(store.fulfilmentModes.includes('COLLECTION')
      ? [
          {
            label: 'Collection',
            value: store.collectionEtaLabel ?? 'Offered',
            sub: ['Free', where].filter(Boolean).join(' · '),
            icon: PATHS.bag,
          },
        ]
      : []),
    ...(next?.bookability?.nextAvailableAt !== undefined
      ? [
          {
            label: 'Next appointment',
            value: nextLabel(next.bookability.nextAvailableAt, now),
            sub: next.title,
            icon: PATHS.calendar,
          },
        ]
      : []),
    {
      label: 'Distance',
      value: miles !== undefined ? `${miles.toFixed(1)} mi` : 'Set your postcode',
      sub: miles !== undefined && place !== undefined ? `From ${place.postcode}` : 'In the header above',
      icon: PATHS.pin,
    },
  ];

  const typeLine = [
    isPharmacy ? 'Pharmacy' : undefined,
    svcList.length > 0
      ? `${String(svcList.length)} ${svcList.length === 1 ? 'service' : 'services'}`
      : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  const productList: readonly OfferCard[] =
    products.status === 'loaded'
      ? [...products.data.results, ...(extra.key === productKey ? extra.offers : [])]
      : [];
  const nextCursor =
    extra.key === productKey && extra.cursor !== undefined
      ? extra.cursor
      : products.status === 'loaded' && extra.key !== productKey
        ? products.data.pageInfo.nextCursor
        : extra.key === productKey
          ? extra.cursor
          : undefined;
  const forms = [
    ...new Set(productList.map((p) => p.product?.presentation).filter((p): p is string => p !== undefined)),
  ];
  const visible =
    form === undefined ? productList : productList.filter((p) => p.product?.presentation === form);

  const loadMore = async (): Promise<void> => {
    if (nextCursor === undefined || productKey === undefined) return;
    const page = await searchStore({
      storefrontId: store.id,
      kind: 'PRODUCT',
      ...(term.length >= 2 ? { q: term } : {}),
      ...(shelf !== undefined ? { categoryId: shelf } : {}),
      sort,
      cursor: nextCursor,
    });
    setExtra((previous) => ({
      key: productKey,
      offers: [...(previous.key === productKey ? previous.offers : []), ...page.results],
      ...(page.pageInfo.nextCursor !== undefined ? { cursor: page.pageInfo.nextCursor } : {}),
    }));
  };

  const pickShelf = (id: string | undefined): void => {
    setShelf(id);
    setForm(undefined);
    setQuery('');
  };

  const share = async (): Promise<void> => {
    const url = `${window.location.origin}/storefronts/${store.slug}`;
    try {
      if (typeof window.navigator.share === 'function') {
        await window.navigator.share({ title: store.name, url });
        return;
      }
      await window.navigator.clipboard.writeText(url);
      flash('Link copied');
    } catch {
      // The visitor closed the share sheet; nothing to say.
    }
  };

  const address = [store.name, where].filter(Boolean).join(', ');
  const directions =
    storeCoords !== undefined
      ? `https://www.google.com/maps/dir/?api=1&destination=${String(storeCoords.latitude)},${String(storeCoords.longitude)}`
      : where !== undefined
        ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
        : undefined;

  const otherShops: readonly ProviderCard[] =
    providers.status === 'loaded' ? providers.data.filter((p) => p.id !== store.id).slice(0, 4) : [];

  return (
    <div className="jt-st">
      <div className="jt-st__main">
        <div className="jt-st__intro">
          <BackLink />
          <div className="jt-st-hero" data-keep="1">
            {store.image !== undefined ? (
              <span
                role="img"
                aria-label={store.name}
                className="jt-st-hero__img"
                style={{ backgroundImage: `url("${store.image.url}")` }}
              />
            ) : (
              <>
                <span aria-hidden="true" className="jt-st-hero__disc" />
                <span aria-hidden="true" className="jt-st-hero__ghost">
                  {initials}
                </span>
              </>
            )}
            <span className="jt-st-hero__logo" style={{ background: PLAQUE }}>
              {initials}
            </span>
            {status !== undefined ? (
              <span className={`jt-st-hero__status${status.open ? '' : ' jt-st-hero__status--shut'}`}>
                <span />
                {status.label}
              </span>
            ) : null}
          </div>
          <div className="jt-st-head">
            <div className="jt-st-head__text">
              <h1>{store.name}</h1>
              <div className="jt-st-head__meta">
                {store.rating !== undefined ? (
                  <a
                    href="#st-reviews"
                    onClick={(event) => {
                      event.preventDefault();
                      goSection('reviews');
                    }}
                  >
                    <Icon d={PATHS.star} size={14} stroke="none" fill="#f5a524" />
                    {store.rating.toFixed(1)}
                    {store.reviewCount !== undefined ? ` (${store.reviewCount.toLocaleString('en-GB')})` : ''}
                  </a>
                ) : null}
                {typeLine !== '' ? <span>{typeLine}</span> : null}
                {where !== undefined ? <span>{where}</span> : null}
              </div>
              {badges.length > 0 ? (
                <div className="jt-st-badges">
                  {badges.map((badge) => (
                    <span key={badge}>
                      <Icon d={PATHS.check} size={12} width={2.6} />
                      {badge}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="jt-st-head__actions">
              <button
                type="button"
                className="jt-st-share"
                aria-label={`Share ${store.name}`}
                onClick={() => void share()}
              >
                <Icon d={PATHS.share} size={16} width={2.2} />
                {!narrow ? <span>Share</span> : null}
              </button>
              <button
                type="button"
                className="jt-st-circle"
                aria-label={`Save ${store.name} (coming soon)`}
                aria-disabled="true"
                title="Coming soon"
              >
                <Icon d={PATHS.heart} size={19} />
              </button>
              <button
                type="button"
                className="jt-st-circle"
                aria-label="Store information"
                onClick={() => goSection('about')}
              >
                <Icon d={PATHS.info} size={19} />
              </button>
            </div>
          </div>
          <div className="jt-st-facts">
            {facts.map((fact) => (
              <div key={fact.label} className="jt-st-fact">
                <span className="jt-st-fact__icon">
                  <Icon d={fact.icon} size={18} />
                </span>
                <span className="jt-st-fact__text">
                  <span>{fact.label}</span>
                  <b>{fact.value}</b>
                  <small>{fact.sub}</small>
                </span>
              </div>
            ))}
          </div>
          <FeesLine store={store} />
          {store.promotions.length > 0 ? (
            <div className="jt-st-offers">
              {store.promotions.map((name) => (
                <div key={name} className="jt-st-offer">
                  <span className="jt-st-offer__icon">
                    <Icon d={PATHS.tag} size={14} stroke="#5c3206" width={2.4} />
                  </span>
                  <span className="jt-st-offer__name">{name}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="jt-st-nav">
          <div role="tablist" aria-label="Store sections" className="jt-st-nav__tabs">
            {tabs.map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={section === id}
                className={section === id ? 'jt-st-nav__tab jt-st-nav__tab--on' : 'jt-st-nav__tab'}
                onClick={() => goSection(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="jt-st-search">
            <Icon d={PATHS.search} size={19} width={2.2} />
            <input
              value={query}
              onChange={(event) => {
                // In-store search covers the whole shop, as the frame's does — not one shelf.
                setQuery(event.target.value);
                if (event.target.value.trim().length >= 2) setShelf(undefined);
              }}
              placeholder={`Search in ${store.name}`}
              aria-label={`Search in ${store.name}`}
            />
            {query.length > 0 ? (
              <button
                type="button"
                className="jt-st-round jt-st-round--sm"
                aria-label="Clear search"
                onClick={() => setQuery('')}
              >
                <Icon d={PATHS.close} size={14} stroke="#17112b" width={2.6} />
              </button>
            ) : null}
          </label>
          {showProducts && store.categories.length > 0 ? (
            <ChipBar store={store} shelf={shelf} onPick={pickShelf} images={images} />
          ) : null}
        </div>

        <div id="st-products" className="jt-st-anchor">
          {!showProducts ? (
            <section aria-label="Categories" className="jt-st-cats">
              <h2 className="jt-st-h2">Shop by category</h2>
              {store.categories.length === 0 ? (
                <p className="jt-st-muted">This store has no products listed yet.</p>
              ) : (
                <div className="jt-st-cats__grid">
                  {store.categories.map((category, index) => (
                    <button
                      key={category.id}
                      type="button"
                      className="jt-st-cat"
                      onClick={() => pickShelf(category.id)}
                    >
                      <span className="jt-st-cat__tile" data-tint={String(index % 6)}>
                        <span
                          role="img"
                          aria-hidden="true"
                          className="jt-st-cat__img"
                          style={
                            images.get(category.id) !== undefined
                              ? { backgroundImage: `url("${images.get(category.id) ?? ''}")` }
                              : undefined
                          }
                        />
                      </span>
                      <span className="jt-st-cat__name">{category.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          ) : (
            <ProductSection
              store={store}
              title={term.length >= 2 ? `Results for “${term}”` : (shelfName(shelf) ?? 'Products')}
              eyebrow={term.length >= 2 ? 'SEARCH RESULTS' : 'MEDICINES & HEALTH PRODUCTS'}
              loaded={products}
              list={visible}
              total={productList.length}
              more={nextCursor !== undefined}
              onMore={() => void loadMore()}
              forms={forms}
              form={form}
              onForm={setForm}
              sort={sort}
              onSort={() =>
                setSort(
                  (s) => SORTS[(SORTS.findIndex(([id]) => id === s) + 1) % SORTS.length]?.[0] ?? 'RELEVANCE',
                )
              }
              query={term}
              shelf={shelfName(shelf)}
              lineFor={lineFor}
              busy={cart.busy}
              onOpen={(offer) =>
                setOverlay(legalOf(offer) === 'POM' ? { kind: 'consult', offer } : { kind: 'product', offer })
              }
              onView={(offer) => setOverlay({ kind: 'product', offer })}
              onAdd={(offer) => void quickAdd(offer)}
              onLess={(line) => void setLine(line, line.quantity - 1)}
              onConsult={(offer) => setOverlay({ kind: 'consult', offer })}
            />
          )}
        </div>

        <section id="st-services" aria-label="Book a service" className="jt-st-anchor jt-st-svc">
          <div className="jt-st-svc__head">
            <h2 className="jt-st-h2">Book a service</h2>
            <span>Appointments at {store.name}</span>
          </div>
          {services.status === 'loading' ? <p className="jt-st-muted">Loading services…</p> : null}
          {services.status === 'loaded' && svcList.length === 0 ? (
            <p className="jt-st-muted">{store.name} has no services to book online yet.</p>
          ) : null}
          {svcList.map((service) => {
            const bookable = service.bookability?.bookable === true;
            const minutes = service.service?.typicalMinutes;
            return (
              <div key={service.id} className="jt-st-svc__row">
                <div className="jt-st-svc__text">
                  <span className="jt-st-svc__name">{service.title}</span>
                  <span className="jt-st-svc__sub">
                    {[service.summary, minutes !== undefined ? `${String(minutes)} min` : undefined]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                <span className="jt-st-svc__price">{priceLabel(service.price)}</span>
                {bookable ? (
                  <button
                    type="button"
                    className="jt-st-svc__btn"
                    onClick={() => setOverlay({ kind: 'book', offer: service })}
                  >
                    Book
                  </button>
                ) : (
                  <AppLink to={`/offers/${service.id}`} className="jt-st-svc__btn jt-st-svc__btn--quiet">
                    View
                  </AppLink>
                )}
              </div>
            );
          })}
        </section>

        {testRows.length > 0 ? (
          <TestsSection rows={testRows} lineFor={lineFor} onAdd={(offer) => void quickAdd(offer)} />
        ) : null}

        <ReviewsSection
          store={store}
          initials={initials}
          reviews={reviews}
          limit={reviewLimit}
          onMore={() => setReviewLimit(50)}
        />

        <section id="st-about" aria-label="About this store" className="jt-st-anchor jt-st-about">
          <h2 className="jt-st-h2">Location &amp; opening hours</h2>
          <div className="jt-st-map">
            <div className="jt-st-map__canvas" role="img" aria-label={`Where ${store.name} is`}>
              <span className="jt-st-map__pin" style={{ background: PLAQUE }}>
                {initials}
              </span>
              <div className="jt-st-map__card">
                <span className="jt-st-map__logo" style={{ background: PLAQUE }}>
                  {initials}
                </span>
                <span className="jt-st-map__text">
                  <b>{store.name}</b>
                  <span>
                    {[where, miles !== undefined ? `${miles.toFixed(1)} mi` : undefined]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </div>
            </div>
            <div className="jt-st-map__actions">
              {directions !== undefined ? (
                <a href={directions} target="_blank" rel="noopener noreferrer" className="jt-st-map__go">
                  <Icon d={PATHS.directions} size={17} stroke="#fff" width={2.2} />
                  Get directions
                </a>
              ) : null}
              {store.publicPhone !== undefined ? (
                <a href={`tel:${store.publicPhone.replace(/\s+/g, '')}`} className="jt-st-map__alt">
                  <Icon d={PATHS.phone} size={16} width={2.2} />
                  Call {store.publicPhone}
                </a>
              ) : null}
              {where !== undefined ? (
                <button
                  type="button"
                  className="jt-st-map__alt"
                  onClick={() => {
                    void window.navigator.clipboard
                      ?.writeText(address)
                      .then(() => flash('Address copied'))
                      .catch(() => undefined);
                  }}
                >
                  <Icon d={PATHS.copy} size={16} width={2.2} />
                  Copy address
                </button>
              ) : null}
            </div>
          </div>
          <div className="jt-st-about__cards">
            {store.openingHours !== undefined && store.openingHours.length > 0 ? (
              <div className="jt-st-card">
                <span className="jt-st-card__kicker">OPENING HOURS</span>
                {hoursTable(store.openingHours, now).map((row) => (
                  <div key={row.day} className={row.today ? 'jt-st-hours jt-st-hours--today' : 'jt-st-hours'}>
                    <span>{row.day}</span>
                    <span>{row.times}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {store.registration !== undefined ? (
              <div className="jt-st-card">
                <span className="jt-st-card__kicker">
                  {isPharmacy ? 'REGISTERED PHARMACY' : 'REGISTRATION'}
                </span>
                <div className="jt-st-reg">
                  <span>
                    {isPharmacy ? 'GPhC premises number' : `${store.registration.regulator} number`}
                  </span>
                  <b>{store.registration.number}</b>
                </div>
                {store.registration.superintendent !== undefined ? (
                  <div className="jt-st-reg">
                    <span>Superintendent pharmacist</span>
                    <b>{store.registration.superintendent}</b>
                  </div>
                ) : null}
                {isPharmacy ? (
                  <a
                    href="https://www.pharmacyregulation.org/registers"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="jt-st-reg__link"
                  >
                    Check the GPhC register
                    <Icon d={PATHS.external} size={13} stroke="currentColor" width={2.4} />
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        {otherShops.length > 0 ? (
          <section aria-label="More places nearby" className="jt-st-near">
            <h2 className="jt-st-h2">More places nearby</h2>
            <div className="jt-st-near__grid">
              {otherShops.map((shop) => (
                <AppLink key={shop.id} to={`/storefronts/${shop.slug}`} className="jt-st-near__card">
                  <span
                    className="jt-st-near__tile"
                    style={
                      shop.image !== undefined
                        ? { backgroundImage: `url("${shop.image.url}")` }
                        : { background: shop.accent ?? PLAQUE }
                    }
                  >
                    <span aria-hidden="true" className="jt-st-near__disc" />
                    <span className="jt-st-near__initials">{shop.initials}</span>
                  </span>
                  <span className="jt-st-near__name">{shop.name}</span>
                  {shop.rating !== undefined ? (
                    <span className="jt-st-near__meta">
                      ★ {shop.rating.toFixed(1)}
                      {shop.reviewCount !== undefined ? ` (${shop.reviewCount.toLocaleString('en-GB')})` : ''}
                    </span>
                  ) : null}
                </AppLink>
              ))}
            </div>
          </section>
        ) : null}
      </div>

      {narrow && drawer ? (
        <div className="jt-st-scrim jt-st-scrim--bk" onClick={() => setDrawer(false)} aria-hidden="true" />
      ) : null}
      <div className="jt-st__rail">
        <div className="jt-st__railin">
          <StoreBasket
            store={store}
            cart={cart.cart}
            busy={cart.busy}
            error={basketError}
            mode={mode}
            onMode={setMode}
            onQuantity={(line, quantity) => void setLine(line, quantity)}
            onClear={() => {
              void cart
                .clear()
                .then(() => flash('Basket emptied'))
                .catch((error: unknown) => setBasketError(refusalOf(error).message));
            }}
            narrow={narrow}
            open={drawer}
            onClose={() => setDrawer(false)}
          />
        </div>
      </div>

      {narrow &&
      !drawer &&
      cart.cart !== undefined &&
      cart.cart.storefrontId === store.id &&
      cart.cart.itemCount > 0 ? (
        <button type="button" className="jt-st-bar" onClick={() => setDrawer(true)}>
          <span className="jt-st-bar__left">
            <span className="jt-st-bar__count">{cart.cart.itemCount}</span>
            View basket
          </span>
          <span>{money(cart.cart.subtotalMinor, cart.cart.currency)}</span>
        </button>
      ) : null}

      {overlay?.kind === 'product' ? (
        <ProductDialog
          offer={overlay.offer}
          storeName={store.name}
          inBasket={lineFor(overlay.offer.id)?.quantity ?? 0}
          busy={cart.busy}
          onClose={() => setOverlay(undefined)}
          onBuy={(quantity, requirement) => void buyFromSheet(overlay.offer, quantity, requirement)}
          onConsult={() => setOverlay({ kind: 'consult', offer: overlay.offer })}
        />
      ) : null}
      {overlay?.kind === 'questions' ? (
        <PharmacyQuestions
          offer={overlay.offer}
          storeName={store.name}
          quantity={overlay.quantity}
          signedIn={signedIn}
          onClose={() => setOverlay(undefined)}
          onAnswered={(consultationId) => add(overlay.offer, overlay.quantity, consultationId)}
        />
      ) : null}
      {overlay?.kind === 'consult' ? (
        <Consultation
          offer={overlay.offer}
          storeName={store.name}
          signedIn={signedIn}
          onClose={() => setOverlay(undefined)}
          onSubmitted={(consultationId) => add(overlay.offer, 1, consultationId)}
        />
      ) : null}
      {overlay?.kind === 'book' ? (
        <BookService
          service={overlay.offer}
          storeName={store.name}
          where={address}
          signedIn={signedIn}
          onClose={() => setOverlay(undefined)}
          onToast={flash}
        />
      ) : null}
      {toast !== '' ? (
        <div className="jt-st-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}

// -- Pieces -------------------------------------------------------------------------

function ChipBar({
  store,
  shelf,
  onPick,
  images,
}: {
  readonly store: Store;
  readonly shelf: string | undefined;
  readonly onPick: (id: string | undefined) => void;
  readonly images: ReadonlyMap<string, string>;
}): JSX.Element {
  const rail = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState(false);
  const scroll = (by: number): void => rail.current?.scrollBy({ left: by, behavior: 'smooth' });
  return (
    <div className="jt-st-chips">
      <button
        type="button"
        className="jt-st-chips__arrow"
        aria-label="Scroll categories back"
        onClick={() => scroll(-240)}
      >
        <Icon d={PATHS.chevronLeft} size={15} width={2.8} />
      </button>
      <div className="jt-st-chips__rail" ref={rail}>
        {store.categories.map((category) => (
          <button
            key={category.id}
            type="button"
            aria-pressed={shelf === category.id}
            className={shelf === category.id ? 'jt-st-chip jt-st-chip--on' : 'jt-st-chip'}
            onClick={() => onPick(category.id)}
          >
            {category.name}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="jt-st-chips__arrow"
        aria-label="Scroll categories forward"
        onClick={() => scroll(240)}
      >
        <Icon d={PATHS.chevronRight} size={15} width={2.8} />
      </button>
      <button
        type="button"
        className={menu ? 'jt-st-chips__all jt-st-chips__all--on' : 'jt-st-chips__all'}
        aria-haspopup="menu"
        aria-expanded={menu}
        aria-label="All categories"
        onClick={() => setMenu((value) => !value)}
      >
        <Icon d={PATHS.list} size={17} stroke="#17112b" width={2.4} />
      </button>
      {menu ? (
        <>
          <div className="jt-st-chips__scrim" onClick={() => setMenu(false)} aria-hidden="true" />
          <div role="menu" aria-label="Categories" className="jt-st-chips__menu">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenu(false);
                onPick(undefined);
              }}
            >
              <span className="jt-st-chips__menuicon">
                <Icon d={PATHS.list} size={15} />
              </span>
              <b>All categories</b>
            </button>
            {store.categories.map((category) => (
              <button
                key={category.id}
                type="button"
                role="menuitem"
                className={shelf === category.id ? 'jt-st-chips__item--on' : ''}
                onClick={() => {
                  setMenu(false);
                  onPick(category.id);
                }}
              >
                <span
                  className="jt-st-chips__menuimg"
                  style={
                    images.get(category.id) !== undefined
                      ? { backgroundImage: `url("${images.get(category.id) ?? ''}")` }
                      : undefined
                  }
                />
                <span className="jt-st-chips__menuname">{category.name}</span>
                <small>{category.offerCount}</small>
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function ProductSection({
  store,
  title,
  eyebrow,
  loaded,
  list,
  total,
  more,
  onMore,
  forms,
  form,
  onForm,
  sort,
  onSort,
  query,
  shelf,
  lineFor,
  busy,
  onOpen,
  onView,
  onAdd,
  onLess,
  onConsult,
}: {
  readonly store: Store;
  readonly title: string;
  readonly eyebrow: string;
  readonly loaded: Loaded<unknown> & { reload(): void };
  readonly list: readonly OfferCard[];
  readonly total: number;
  readonly more: boolean;
  readonly onMore: () => void;
  readonly forms: readonly string[];
  readonly form: string | undefined;
  readonly onForm: (form: string | undefined) => void;
  readonly sort: Sort;
  readonly onSort: () => void;
  readonly query: string;
  readonly shelf: string | undefined;
  readonly lineFor: (listingId: string) => CartLine | undefined;
  readonly busy: boolean;
  readonly onOpen: (offer: OfferCard) => void;
  readonly onView: (offer: OfferCard) => void;
  readonly onAdd: (offer: OfferCard) => void;
  readonly onLess: (line: CartLine) => void;
  readonly onConsult: (offer: OfferCard) => void;
}): JSX.Element {
  const count = `${String(total)}${more ? '+' : ''} ${total === 1 ? 'product' : 'products'}`;
  const delivers = offersDelivery(store);
  return (
    <section aria-label={title} className="jt-st-prod">
      <div className="jt-st-prod__banner">
        <span aria-hidden="true" className="jt-st-prod__disc" />
        <span className="jt-st-prod__eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
        <span className="jt-st-prod__sub">
          {count} · {delivers ? `delivered from ${store.name}` : 'collect in store'}
        </span>
      </div>
      {forms.length > 1 ? (
        <div role="tablist" aria-label={`Filter ${title}`} className="jt-st-subs">
          <button
            type="button"
            role="tab"
            aria-selected={form === undefined}
            className={form === undefined ? 'jt-st-sub jt-st-sub--on' : 'jt-st-sub'}
            onClick={() => onForm(undefined)}
          >
            All
          </button>
          {forms.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={form === f}
              className={form === f ? 'jt-st-sub jt-st-sub--on' : 'jt-st-sub'}
              onClick={() => onForm(f)}
            >
              {presentationLabel(f)}
            </button>
          ))}
        </div>
      ) : null}
      <div className="jt-st-ask" aria-disabled="true">
        <span className="jt-st-ask__icon">
          <Icon d={PATHS.stethoscope} size={24} />
        </span>
        <span className="jt-st-ask__text">
          <b>Not sure which to choose?</b>
          <span>
            Pharmacist advice online · <Soon />
          </span>
        </span>
      </div>
      <div className="jt-st-prod__bar">
        <span>{`${String(list.length)} ${list.length === 1 ? 'item' : 'items'}`}</span>
        <button type="button" className="jt-st-sort" onClick={onSort}>
          <Icon d={PATHS.sort} size={15} stroke="#17112b" width={2.2} />
          {SORTS.find(([id]) => id === sort)?.[1]}
        </button>
      </div>
      {loaded.status === 'loading' ? <p className="jt-st-muted">Loading…</p> : null}
      {loaded.status === 'error' ? (
        <p className="jt-st-alert" role="alert">
          {loaded.message}{' '}
          <button type="button" className="jt-st-linkbtn" onClick={loaded.reload}>
            Try again
          </button>
        </p>
      ) : null}
      {loaded.status === 'loaded' && list.length === 0 ? (
        <p className="jt-st-hintbox">
          {query.length > 0
            ? `Nothing matches “${query}” here. Try another word.`
            : `Nothing on this shelf right now.`}
        </p>
      ) : null}
      <div className="jt-st-grid">
        {list.map((offer) => (
          <ProductCard
            key={offer.id}
            offer={offer}
            shelf={shelf}
            line={lineFor(offer.id)}
            busy={busy}
            onOpen={() => onOpen(offer)}
            onView={() => onView(offer)}
            onAdd={() => onAdd(offer)}
            onLess={onLess}
            onConsult={() => onConsult(offer)}
          />
        ))}
      </div>
      {more ? (
        <button type="button" className="jt-st-more" onClick={onMore}>
          Show more products
        </button>
      ) : null}
    </section>
  );
}

function ProductCard({
  offer,
  shelf,
  line,
  busy,
  onOpen,
  onView,
  onAdd,
  onLess,
  onConsult,
}: {
  readonly offer: OfferCard;
  readonly shelf: string | undefined;
  readonly line: CartLine | undefined;
  readonly busy: boolean;
  readonly onOpen: () => void;
  readonly onView: () => void;
  readonly onAdd: () => void;
  readonly onLess: (line: CartLine) => void;
  readonly onConsult: () => void;
}): JSX.Element {
  const legal = legalOf(offer);
  const badge = savingBadge(offer.price);
  const out = offer.stock?.inStock === false;
  const size = sizeOf(offer);
  return (
    <article className="jt-st-card-p">
      <div
        className="jt-st-card-p__media"
        role="button"
        tabIndex={0}
        aria-label={`View ${offer.title}`}
        style={offer.image !== undefined ? { backgroundImage: `url("${offer.image.url}")` } : undefined}
        onClick={onView}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onView();
          }
        }}
      >
        {badge !== undefined ? <span className="jt-st-card-p__badge">{badge}</span> : null}
        {out ? <span className="jt-st-card-p__badge jt-st-card-p__badge--out">OUT OF STOCK</span> : null}
        {legal !== 'POM' && !out && line === undefined ? (
          <button
            type="button"
            className="jt-st-card-p__add"
            aria-label={`Add ${offer.title}`}
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onAdd();
            }}
          >
            <Icon d={PATHS.plus} size={16} stroke="#fff" width={2.8} />
          </button>
        ) : null}
        {line !== undefined ? (
          <div className="jt-st-card-p__qty" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              disabled={busy}
              aria-label={`Remove one ${offer.title}`}
              onClick={() => onLess(line)}
            >
              <Icon d={PATHS.minus} size={14} stroke="#fff" width={2.8} />
            </button>
            <span aria-live="polite">{line.quantity}</span>
            <button
              type="button"
              disabled={busy || line.consultationId !== undefined}
              aria-label={`Add another ${offer.title}`}
              onClick={onAdd}
            >
              <Icon d={PATHS.plus} size={14} stroke="#fff" width={2.8} />
            </button>
          </div>
        ) : null}
      </div>
      <div className="jt-st-card-p__body">
        <span className="jt-st-eyebrow">{eyebrowOf(offer, shelf)}</span>
        <span className="jt-st-card-p__name" onClick={onOpen}>
          {offer.title}
        </span>
        {size !== undefined ? <span className="jt-st-card-p__size">{size}</span> : null}
        <span className="jt-st-card-p__price">{priceLabel(offer.price)}</span>
        {legal === 'POM' ? (
          <button type="button" className="jt-st-card-p__consult" onClick={onConsult}>
            Start consultation
          </button>
        ) : null}
      </div>
    </article>
  );
}

function TestsSection({
  rows,
  lineFor,
  onAdd,
}: {
  readonly rows: readonly TestRow[];
  readonly lineFor: (listingId: string) => CartLine | undefined;
  readonly onAdd: (offer: OfferCard) => void;
}): JSX.Element {
  const [where, setWhere] = useState<'all' | 'home' | 'clinic'>('all');
  const [shelf, setShelf] = useState<string | undefined>(undefined);
  const home = rows.filter((r) => r.test.collection !== 'CLINIC');
  const clinic = rows.filter((r) => r.test.collection !== 'HOME');
  const scoped = where === 'home' ? home : where === 'clinic' ? clinic : rows;
  const shelves = [...new Set(scoped.map((r) => r.test.shelf))];
  const list = shelf === undefined ? scoped : scoped.filter((r) => r.test.shelf === shelf);
  return (
    <section id="st-tests" aria-label="Tests and health checks" className="jt-st-anchor jt-st-tests">
      <div className="jt-st-svc__head">
        <h2 className="jt-st-h2">Tests &amp; health checks</h2>
        <span>
          Home test kits posted to you, or a sample taken at a clinic. Results in your Just Treated account.
        </span>
      </div>
      <div role="tablist" aria-label="Where" className="jt-st-where">
        {(
          [
            ['all', `All tests · ${String(rows.length)}`],
            ['home', `At home · ${String(home.length)}`],
            ['clinic', `At a clinic · ${String(clinic.length)}`],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={where === id}
            className={where === id ? 'jt-st-where__on' : ''}
            onClick={() => {
              setWhere(id);
              setShelf(undefined);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="jt-st-tests__shelves">
        {shelves.map((name) => {
          const n = scoped.filter((r) => r.test.shelf === name).length;
          return (
            <button
              key={name}
              type="button"
              aria-pressed={shelf === name}
              className={shelf === name ? 'jt-st-tshelf jt-st-tshelf--on' : 'jt-st-tshelf'}
              onClick={() => setShelf(shelf === name ? undefined : name)}
            >
              <span className="jt-st-tshelf__icon">
                <Icon d={PATHS.flask} size={17} />
              </span>
              <span className="jt-st-tshelf__text">
                <b>{name}</b>
                <small>
                  {n} {n === 1 ? 'test' : 'tests'}
                </small>
              </span>
            </button>
          );
        })}
      </div>
      <div className="jt-st-tests__list">
        {list.map((row) => {
          const homeKit = row.test.collection !== 'CLINIC';
          const inBasket = lineFor(row.offer.id) !== undefined;
          return (
            <div key={row.offer.id} className="jt-st-test">
              <span className="jt-st-test__icon">
                <Icon d={homeKit ? PATHS.drop : PATHS.pulse} size={20} />
              </span>
              <div className="jt-st-test__text">
                <span className="jt-st-test__name">
                  {row.offer.title}
                  <span className={homeKit ? 'jt-st-test__tag' : 'jt-st-test__tag jt-st-test__tag--clinic'}>
                    {homeKit ? 'Home kit' : 'At a clinic'}
                  </span>
                </span>
                {row.test.turnaround !== undefined ? (
                  <span className="jt-st-test__sub">{row.test.turnaround}</span>
                ) : null}
              </div>
              <span className="jt-st-test__price">{priceLabel(row.offer.price)}</span>
              {homeKit ? (
                <button
                  type="button"
                  className="jt-st-test__btn"
                  disabled={inBasket}
                  onClick={() => onAdd(row.offer)}
                >
                  {inBasket ? 'In basket' : 'Order kit'}
                </button>
              ) : (
                <AppLink to={`/offers/${row.offer.id}`} className="jt-st-test__btn">
                  View
                </AppLink>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ReviewsSection({
  store,
  initials,
  reviews,
  limit,
  onMore,
}: {
  readonly store: Store;
  readonly initials: string;
  readonly reviews: Loaded<readonly Review[]>;
  readonly limit: number;
  readonly onMore: () => void;
}): JSX.Element {
  const list = reviews.status === 'loaded' ? reviews.data : [];
  // A star split only when every review is on the page — a split of a sample would be a guess.
  const complete =
    reviews.status === 'loaded' &&
    list.length < limit &&
    store.reviewCount === list.length &&
    list.length > 0;
  const split = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    count: list.filter((r) => Math.round(r.rating) === stars).length,
  }));
  return (
    <section id="st-reviews" aria-label="Reviews" className="jt-st-anchor jt-st-rev">
      <div className="jt-st-rev__head">
        <h2 className="jt-st-h2">Reviews</h2>
      </div>
      {store.rating !== undefined ? (
        <div className="jt-st-rev__summary">
          <div className="jt-st-rev__score">
            <span className="jt-st-rev__big">{store.rating.toFixed(1)}</span>
            <Stars count={store.rating} size={17} />
            {store.reviewCount !== undefined ? (
              <span className="jt-st-rev__based">
                Based on {store.reviewCount.toLocaleString('en-GB')} reviews
              </span>
            ) : null}
          </div>
          {complete ? (
            <div className="jt-st-rev__split">
              {split.map((row) => (
                <div key={row.stars} className="jt-st-rev__bar">
                  <span>{row.stars}</span>
                  <span className="jt-st-rev__track">
                    <span style={{ width: `${String(Math.round((row.count / list.length) * 100))}%` }} />
                  </span>
                  <span>{row.count}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {reviews.status === 'loaded' && list.length === 0 ? (
        <p className="jt-st-muted">No written reviews yet.</p>
      ) : null}
      <div className="jt-st-rev__list">
        {list.map((review) => (
          <article key={review.id} className="jt-st-review-a">
            <div className="jt-st-review-a__top">
              <span className="jt-st-review-a__av">{initialsOf(review.reviewer)}</span>
              <div className="jt-st-review-a__who">
                <b>{review.reviewer}</b>
                <span>
                  <Icon d={PATHS.check} size={12} stroke="#1f9d6b" width={2.8} />
                  Verified {/booking|appointment/i.test(review.subject.type) ? 'appointment' : 'order'}
                </span>
              </div>
              <span className="jt-st-review-a__date">
                {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(
                  new Date(review.createdAt),
                )}
              </span>
            </div>
            <Stars count={review.rating} />
            {review.subject.label !== undefined ? (
              <span className="jt-st-review-a__svc">
                <b>Service:</b> {review.subject.label}
              </span>
            ) : null}
            <p>{review.body}</p>
            {review.response !== null ? (
              <div className="jt-st-review-a__reply">
                <span>
                  <span className="jt-st-review-a__shop" style={{ background: PLAQUE }}>
                    {initials}
                  </span>
                  Response from {store.name}
                </span>
                <span>{review.response.body}</span>
              </div>
            ) : null}
          </article>
        ))}
      </div>
      {reviews.status === 'loaded' && list.length >= limit && limit < 50 ? (
        <button type="button" className="jt-st-more" onClick={onMore}>
          Show more reviews
        </button>
      ) : null}
    </section>
  );
}
